import { combineReducers } from 'redux';

import { CL, CE, CO, CC, CI, COT, CS, CCT, CT, CRT, BOARD_STATUS_KEYS } from './cateringTypes';
import type {
  CateringLeadsState,
  CateringLeadsBoardState,
  LeadBoardColumns,
  BoardColumnState,
  CateringEstimatesState,
  CateringOrdersState,
  CateringCustomersState,
  CateringInvoicesState,
  CateringOrderTypesState,
  CateringSettingsState,
  CateringCommTemplatesState,
  CateringTaxesState,
  CateringTaxesAction,
  CateringResolvedTemplateState,
} from './cateringTypes';
import { resolvedTemplateKey } from './cateringTypes';
import type {
  CateringLeadsAction,
  CateringEstimatesAction,
  CateringOrdersAction,
  CateringCustomersAction,
  CateringInvoicesAction,
  CateringCommTemplatesAction,
  CateringResolvedTemplateAction,
} from './cateringActions';
import * as AC from './cateringActions';
import { cateringPackagesReducer } from './cateringPackagesIndex';
import { cateringAccessReducer } from './cateringAccessIndex';
import type { CateringLead, LeadStatus, InvoiceStatus } from '../../components/catering/data/types';

// ════════════════════════════════════════════════════════════════════════════════
// LEADS REDUCER
// ════════════════════════════════════════════════════════════════════════════════

function emptyBoardColumn(): BoardColumnState {
  return { items: [], page: 1, total: 0, hasMore: false, loadingMore: false, error: null };
}

function emptyBoardColumns(): LeadBoardColumns {
  return BOARD_STATUS_KEYS.reduce((acc, s) => {
    acc[s] = emptyBoardColumn();
    return acc;
  }, {} as LeadBoardColumns);
}

const boardInitialState: CateringLeadsBoardState = {
  columns: emptyBoardColumns(),
  loading: false,
  error: null,
  loadedLocationId: null,
  filterKey: '',
};

/** Locate which column currently holds a lead. */
function findBoardStatus(columns: LeadBoardColumns, leadId: string): LeadStatus | null {
  for (const s of BOARD_STATUS_KEYS) {
    if (columns[s].items.some((l) => l.id === leadId)) return s;
  }
  return null;
}

/**
 * Apply a partial change to a card in the board, repositioning it (and adjusting
 * column totals) when its status changes. No-op if the card isn't loaded.
 */
function applyBoardChange(
  board: CateringLeadsBoardState,
  leadId: string,
  changes: Partial<CateringLead>,
): CateringLeadsBoardState {
  const fromStatus = findBoardStatus(board.columns, leadId);
  if (!fromStatus) return board;

  const existing = board.columns[fromStatus].items.find((l) => l.id === leadId)!;
  const updated = { ...existing, ...changes, updatedAt: new Date().toISOString() };
  const toStatus = (changes.status as LeadStatus | undefined) ?? fromStatus;
  const columns = { ...board.columns };

  if (toStatus === fromStatus) {
    columns[fromStatus] = {
      ...columns[fromStatus],
      items: columns[fromStatus].items.map((l) => (l.id === leadId ? updated : l)),
    };
    return { ...board, columns };
  }

  columns[fromStatus] = {
    ...columns[fromStatus],
    items: columns[fromStatus].items.filter((l) => l.id !== leadId),
    total: Math.max(0, columns[fromStatus].total - 1),
  };
  columns[toStatus] = {
    ...columns[toStatus],
    items: [updated, ...columns[toStatus].items.filter((l) => l.id !== leadId)],
    total: columns[toStatus].total + 1,
  };
  return { ...board, columns };
}

/** Remove a card from whichever column holds it, decrementing that column's total. */
function removeBoardCard(board: CateringLeadsBoardState, leadId: string): CateringLeadsBoardState {
  const fromStatus = findBoardStatus(board.columns, leadId);
  if (!fromStatus) return board;
  return {
    ...board,
    columns: {
      ...board.columns,
      [fromStatus]: {
        ...board.columns[fromStatus],
        items: board.columns[fromStatus].items.filter((l) => l.id !== leadId),
        total: Math.max(0, board.columns[fromStatus].total - 1),
      },
    },
  };
}

/** Insert a newly created card at the top of its status column. */
function addBoardCard(board: CateringLeadsBoardState, lead: CateringLead): CateringLeadsBoardState {
  if (!board.loadedLocationId) return board;
  const s: LeadStatus = BOARD_STATUS_KEYS.includes(lead.status) ? lead.status : 'new';
  const col = board.columns[s];
  return {
    ...board,
    columns: {
      ...board.columns,
      [s]: { ...col, items: [lead, ...col.items], total: col.total + 1 },
    },
  };
}

const leadsInitialState: CateringLeadsState = {
  leads: [],
  linkableLeads: [],
  linkableLoading: false,
  linkableLoadedLocationId: null,
  loading: false,
  detailLoading: false,
  detail: null,
  detailError: null,
  error: null,
  loadedLocationId: null,
  lastFetchedAt: null,
  saving: false,
  savingError: null,
  lastConvertedOrderId: null,
  lastStatusChange: null,
  lastStatusError: null,
  analytics: null,
  analyticsLoading: false,
  analyticsError: null,
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
  board: boardInitialState,
};

export function cateringLeadsReducer(
  state: CateringLeadsState = leadsInitialState,
  action: CateringLeadsAction,
): CateringLeadsState {
  switch (action.type) {
    case CL.FETCH_REQUEST:
      return { ...state, loading: true, error: null };

    case CL.LINKABLE_FETCH_REQUEST:
      return { ...state, linkableLoading: true };

    case CL.LINKABLE_FETCH_SUCCESS:
      return {
        ...state,
        linkableLeads: action.payload.leads,
        linkableLoadedLocationId: action.payload.locationId,
        linkableLoading: false,
      };

    case CL.LINKABLE_FETCH_FAILURE:
      return { ...state, linkableLoading: false };

    case CL.FETCH_SUCCESS: {
      const { page } = action.payload;
      return {
        ...state,
        loading: false,
        leads: action.payload.leads,
        loadedLocationId: action.payload.locationId,
        lastFetchedAt: Date.now(),
        page: page.page,
        limit: page.limit,
        total: page.total,
        totalPages: page.totalPages,
      };
    }

    case CL.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    case CL.FETCH_DETAIL_REQUEST:
      // Keep the current detail if re-fetching the same lead (avoids a blank flash).
      return {
        ...state,
        detailLoading: true,
        detailError: null,
        detail: state.detail && state.detail.id === action.payload.leadId ? state.detail : null,
      };

    case CL.FETCH_DETAIL_SUCCESS: {
      const exists = state.leads.some((l) => l.id === action.payload.lead.id);
      return {
        ...state,
        detailLoading: false,
        detail: action.payload.lead,
        leads: exists
          ? state.leads.map((l) => l.id === action.payload.lead.id ? action.payload.lead : l)
          : [...state.leads, action.payload.lead],
      };
    }

    case CL.FETCH_DETAIL_FAILURE:
      return { ...state, detailLoading: false, detailError: action.payload.error };

    case CL.CREATE_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CL.CREATE_SUCCESS: {
      const nextTotal = state.total + 1;
      return {
        ...state,
        saving: false,
        leads: [action.payload.lead, ...state.leads],
        total: nextTotal,
        totalPages: Math.ceil(nextTotal / state.limit) || 1,
        board: addBoardCard(state.board, action.payload.lead),
      };
    }

    case CL.CREATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CL.UPDATE_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CL.UPDATE_SUCCESS:
      return {
        ...state,
        saving: false,
        leads: state.leads.map((l) =>
          l.id === action.payload.leadId
            ? { ...l, ...action.payload.changes, updatedAt: new Date().toISOString() }
            : l,
        ),
        detail: state.detail && state.detail.id === action.payload.leadId
          ? { ...state.detail, ...action.payload.changes, updatedAt: new Date().toISOString() }
          : state.detail,
        board: applyBoardChange(state.board, action.payload.leadId, action.payload.changes),
      };

    case CL.UPDATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CL.STATUS_REQUEST:
      return { ...state, saving: true, savingError: null, lastStatusChange: null, lastStatusError: null };

    case CL.STATUS_SUCCESS:
      return {
        ...state,
        saving: false,
        lastStatusChange: { leadId: action.payload.leadId, status: action.payload.status, prevStatus: action.payload.prevStatus },
        leads: state.leads.map((l) =>
          l.id === action.payload.leadId
            ? { ...l, status: action.payload.status, updatedAt: new Date().toISOString() }
            : l,
        ),
        detail: state.detail && state.detail.id === action.payload.leadId
          ? { ...state.detail, status: action.payload.status, updatedAt: new Date().toISOString() }
          : state.detail,
        board: applyBoardChange(state.board, action.payload.leadId, { status: action.payload.status }),
      };

    case CL.STATUS_FAILURE:
      return {
        ...state,
        saving: false,
        lastStatusError: action.payload.error,
        leads: state.leads.map((l) =>
          l.id === action.payload.leadId
            ? { ...l, status: action.payload.prevStatus, updatedAt: new Date().toISOString() }
            : l,
        ),
        detail: state.detail && state.detail.id === action.payload.leadId
          ? { ...state.detail, status: action.payload.prevStatus, updatedAt: new Date().toISOString() }
          : state.detail,
        board: applyBoardChange(state.board, action.payload.leadId, { status: action.payload.prevStatus }),
      };

    case CL.CONVERT_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CL.CONVERT_SUCCESS:
      return {
        ...state,
        saving: false,
        lastConvertedOrderId: action.payload.orderId,
        leads: state.leads.map((l) =>
          l.id === action.payload.leadId
            ? {
                ...l,
                status: 'won' as const,
                convertedOrderId: action.payload.orderId,
                updatedAt: new Date().toISOString(),
              }
            : l,
        ),
        detail: state.detail && state.detail.id === action.payload.leadId
          ? { ...state.detail, status: 'won' as const, convertedOrderId: action.payload.orderId, updatedAt: new Date().toISOString() }
          : state.detail,
        board: applyBoardChange(state.board, action.payload.leadId, {
          status: 'won',
          convertedOrderId: action.payload.orderId,
        }),
      };

    case CL.CONVERT_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CL.DELETE_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CL.DELETE_SUCCESS: {
      const nextTotal = Math.max(0, state.total - 1);
      return {
        ...state,
        saving: false,
        leads: state.leads.filter((l) => l.id !== action.payload.leadId),
        detail: state.detail && state.detail.id === action.payload.leadId ? null : state.detail,
        total: nextTotal,
        totalPages: Math.ceil(nextTotal / state.limit) || 1,
        board: removeBoardCard(state.board, action.payload.leadId),
      };
    }

    case CL.DELETE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CL.DELETE_LOCAL: {
      const nextTotal = Math.max(0, state.total - 1);
      return {
        ...state,
        leads: state.leads.filter((l) => l.id !== action.payload.leadId),
        total: nextTotal,
        totalPages: Math.ceil(nextTotal / state.limit) || 1,
        board: removeBoardCard(state.board, action.payload.leadId),
      };
    }

    case CL.OPTIMISTIC_UPDATE:
      return {
        ...state,
        leads: state.leads.map((l) =>
          l.id === action.payload.leadId
            ? { ...l, ...action.payload.changes, updatedAt: new Date().toISOString() }
            : l,
        ),
        board: applyBoardChange(state.board, action.payload.leadId, action.payload.changes),
      };

    case CL.CLEAR_CONVERTED:
      return { ...state, lastConvertedOrderId: null };

    case CL.CLEAR_STATUS_FEEDBACK:
      return { ...state, lastStatusChange: null, lastStatusError: null };

    case CL.FETCH_ANALYTICS_REQUEST:
      return { ...state, analyticsLoading: true, analyticsError: null };

    case CL.FETCH_ANALYTICS_SUCCESS:
      return { ...state, analyticsLoading: false, analytics: action.payload.analytics };

    case CL.FETCH_ANALYTICS_FAILURE:
      return { ...state, analyticsLoading: false, analyticsError: action.payload.error };

    // ── Kanban board ──────────────────────────────────────────────────────────
    case CL.BOARD_FETCH_REQUEST:
      return {
        ...state,
        board: { ...state.board, loading: true, error: null, filterKey: action.payload.filterKey },
      };

    case CL.BOARD_FETCH_SUCCESS: {
      const columns = emptyBoardColumns();
      for (const col of action.payload.columns) {
        columns[col.status] = {
          items: col.items,
          page: col.page,
          total: col.total,
          hasMore: col.hasMore,
          loadingMore: false,
          error: null,
        };
      }
      return {
        ...state,
        board: {
          columns,
          loading: false,
          error: null,
          loadedLocationId: action.payload.locationId,
          filterKey: action.payload.filterKey,
        },
      };
    }

    case CL.BOARD_FETCH_FAILURE:
      return { ...state, board: { ...state.board, loading: false, error: action.payload.error } };

    case CL.BOARD_COLUMN_REQUEST:
      return {
        ...state,
        board: {
          ...state.board,
          columns: {
            ...state.board.columns,
            [action.payload.status]: {
              ...state.board.columns[action.payload.status],
              loadingMore: true,
              error: null,
            },
          },
        },
      };

    case CL.BOARD_COLUMN_SUCCESS: {
      const { status, items, page, total, hasMore } = action.payload;
      const col = state.board.columns[status];
      // Guard against stale/duplicate pages: only append pages beyond the current one.
      if (page <= col.page && col.items.length > 0) {
        return {
          ...state,
          board: {
            ...state.board,
            columns: {
              ...state.board.columns,
              [status]: { ...col, loadingMore: false, total, hasMore },
            },
          },
        };
      }
      const seen = new Set(col.items.map((l) => l.id));
      const appended = items.filter((l) => !seen.has(l.id));
      return {
        ...state,
        board: {
          ...state.board,
          columns: {
            ...state.board.columns,
            [status]: {
              items: [...col.items, ...appended],
              page,
              total,
              hasMore,
              loadingMore: false,
              error: null,
            },
          },
        },
      };
    }

    case CL.BOARD_COLUMN_FAILURE:
      return {
        ...state,
        board: {
          ...state.board,
          columns: {
            ...state.board.columns,
            [action.payload.status]: {
              ...state.board.columns[action.payload.status],
              loadingMore: false,
              error: action.payload.error,
            },
          },
        },
      };

    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// ESTIMATES REDUCER
// ════════════════════════════════════════════════════════════════════════════════

const estimatesInitialState: CateringEstimatesState = {
  estimates: [],
  loading: false,
  error: null,
  loadedLocationId: null,
  lastFetchedAt: null,
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
  saving: false,
  savingError: null,
  catalogItems: [],
  catalogItemsLoading: false,
  catalogItemsLoaded: false,
  detail: null,
  detailLoading: false,
  detailError: null,
  sending: false,
  sendingError: null,
  lastSendResult: null,
  statusUpdating: false,
  statusUpdateError: null,
};

export function cateringEstimatesReducer(
  state: CateringEstimatesState = estimatesInitialState,
  action: CateringEstimatesAction,
): CateringEstimatesState {
  switch (action.type) {
    case CE.FETCH_REQUEST:
      return { ...state, loading: true, error: null };

    case CE.FETCH_SUCCESS: {
      const { page } = action.payload;
      return {
        ...state,
        loading: false,
        estimates: action.payload.estimates,
        loadedLocationId: action.payload.locationId,
        lastFetchedAt: Date.now(),
        page: page.page,
        limit: page.limit,
        total: page.total,
        totalPages: page.totalPages,
      };
    }

    case CE.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    case CE.DELETE_LOCAL:
    case CE.DELETE_SUCCESS: {
      const nextTotal = Math.max(0, state.total - 1);
      return {
        ...state,
        estimates: state.estimates.filter((e) => e.id !== action.payload.estimateId),
        total: nextTotal,
        totalPages: Math.ceil(nextTotal / state.limit) || 1,
      };
    }

    case CE.DELETE_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CE.DELETE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CE.CREATE_REQUEST:
    case CE.UPDATE_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CE.CREATE_SUCCESS: {
      const nextTotal = state.total + 1;
      return {
        ...state,
        saving: false,
        estimates: [action.payload.estimate, ...state.estimates],
        total: nextTotal,
        totalPages: Math.ceil(nextTotal / state.limit) || 1,
      };
    }

    case CE.UPDATE_SUCCESS:
      return {
        ...state,
        saving: false,
        estimates: state.estimates.map((e) =>
          e.id === action.payload.estimate.id ? { ...e, ...action.payload.estimate } : e,
        ),
      };

    case CE.CREATE_FAILURE:
    case CE.UPDATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CE.FETCH_CATALOG_ITEMS_REQUEST:
      return { ...state, catalogItemsLoading: true };

    case CE.FETCH_CATALOG_ITEMS_SUCCESS:
      return {
        ...state,
        catalogItemsLoading: false,
        catalogItemsLoaded: true,
        catalogItems: action.payload.items,
      };

    case CE.FETCH_CATALOG_ITEMS_FAILURE:
      return { ...state, catalogItemsLoading: false };

    case CE.FETCH_DETAIL_REQUEST:
      return { ...state, detailLoading: true, detailError: null, detail: null };

    case CE.FETCH_DETAIL_SUCCESS:
      return { ...state, detailLoading: false, detail: action.payload.estimate };

    case CE.FETCH_DETAIL_FAILURE:
      return { ...state, detailLoading: false, detailError: action.payload.error };

    case CE.SEND_REQUEST:
      return { ...state, sending: true, sendingError: null, lastSendResult: null };

    case CE.SEND_SUCCESS: {
      const { estimateId, sent, skipped, status, sentAt } = action.payload;
      const lastSendResult = { sent, skipped };
      // Nothing went out: the document keeps its previous status — never flip to 'sent' here.
      if (!sent) return { ...state, sending: false, lastSendResult };
      const apply = <T extends { status: any; sentAt: any }>(e: T): T =>
        ({ ...e, status: status as T['status'], sentAt: sentAt ?? e.sentAt });
      return {
        ...state,
        sending: false,
        lastSendResult,
        estimates: state.estimates.map((e) => (e.id === estimateId ? apply(e) : e)),
        detail: state.detail?.id === estimateId ? apply(state.detail) : state.detail,
      };
    }

    case CE.SEND_FAILURE:
      return { ...state, sending: false, sendingError: action.payload.error, lastSendResult: null };

    case CE.UPDATE_STATUS_REQUEST:
      return { ...state, statusUpdating: true, statusUpdateError: null };

    case CE.UPDATE_STATUS_SUCCESS: {
      const { estimateId, status, updatedAt } = action.payload;
      const applyStatus = (e: any) =>
        e.id === estimateId ? { ...e, status, updatedAt } : e;
      return {
        ...state,
        statusUpdating: false,
        estimates: state.estimates.map(applyStatus),
        detail: state.detail?.id === estimateId ? applyStatus(state.detail) : state.detail,
      };
    }

    case CE.UPDATE_STATUS_FAILURE:
      return { ...state, statusUpdating: false, statusUpdateError: action.payload.error };

    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// ORDERS REDUCER
// ════════════════════════════════════════════════════════════════════════════════

const ordersInitialState: CateringOrdersState = {
  orders: [],
  loading: false,
  detailLoading: false,
  detail: null,
  detailError: null,
  error: null,
  loadedLocationId: null,
  dayCapacity: null,
  dayCapacityLoading: false,
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
  saving: false,
  savingError: null,
  lastWarning: null,
  sending: false,
  sendingError: null,
};

function upsertOrder(orders: CateringOrdersState['orders'], next: CateringOrdersState['orders'][number]) {
  const exists = orders.some((o) => o.id === next.id);
  return exists ? orders.map((o) => (o.id === next.id ? next : o)) : [next, ...orders];
}

export function cateringOrdersReducer(
  state: CateringOrdersState = ordersInitialState,
  action: CateringOrdersAction,
): CateringOrdersState {
  switch (action.type) {
    case CO.FETCH_REQUEST:
      return { ...state, loading: true, error: null };

    case CO.DAY_CAPACITY_REQUEST:
      return { ...state, dayCapacityLoading: true };
    case CO.DAY_CAPACITY_SUCCESS:
      return { ...state, dayCapacityLoading: false, dayCapacity: action.payload.capacity };
    case CO.DAY_CAPACITY_FAILURE:
      return { ...state, dayCapacityLoading: false, dayCapacity: null };

    case CO.FETCH_SUCCESS: {
      const p = action.payload.page;
      // The list response is shallow (no items/packages). If a full order detail already arrived
      // before this list response, keep its items/packages instead of wiping them.
      const orders = action.payload.orders.map((o) => {
        const prev = state.orders.find((x) => x.id === o.id);
        if (!prev) return o;
        const keepItems = (prev.items?.length ?? 0) > 0 && (o.items?.length ?? 0) === 0;
        const keepPackages = (prev.packages?.length ?? 0) > 0 && (o.packages?.length ?? 0) === 0;
        return keepItems || keepPackages
          ? { ...o, items: keepItems ? prev.items : o.items, packages: keepPackages ? prev.packages : o.packages }
          : o;
      });
      return {
        ...state,
        loading: false,
        orders,
        loadedLocationId: action.payload.locationId,
        total: p?.total ?? action.payload.total,
        page: p?.page ?? state.page,
        limit: p?.limit ?? state.limit,
        totalPages: p?.totalPages ?? state.totalPages,
      };
    }

    case CO.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    case CO.FETCH_DETAIL_REQUEST:
      return {
        ...state,
        detailLoading: true,
        detailError: null,
        detail: state.detail && state.detail.id === action.payload.orderId ? state.detail : null,
      };

    case CO.FETCH_DETAIL_SUCCESS:
      return {
        ...state,
        detailLoading: false,
        detail: action.payload.order,
        orders: upsertOrder(state.orders, action.payload.order),
      };

    case CO.FETCH_DETAIL_FAILURE:
      return { ...state, detailLoading: false, detailError: action.payload.error };

    case CO.CREATE_REQUEST:
      return { ...state, saving: true, savingError: null, lastWarning: null };

    case CO.CREATE_SUCCESS:
      return {
        ...state,
        saving: false,
        detail: action.payload.order,
        orders: [action.payload.order, ...state.orders],
      };

    case CO.CREATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CO.UPDATE_REQUEST:
      return { ...state, saving: true, savingError: null, lastWarning: null };

    case CO.UPDATE_SUCCESS:
      return {
        ...state,
        saving: false,
        lastWarning: null,
        detail: action.payload.order,
        orders: upsertOrder(state.orders, action.payload.order),
      };

    case CO.UPDATE_WARNING:
      return { ...state, saving: false, lastWarning: action.payload.warning };

    case CO.UPDATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CO.ACTION_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CO.ACTION_SUCCESS:
      return {
        ...state,
        saving: false,
        detail: action.payload.order,
        orders: upsertOrder(state.orders, action.payload.order),
      };

    case CO.ACTION_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CO.STATUS_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CO.STATUS_SUCCESS:
      return {
        ...state,
        saving: false,
        orders: state.orders.map((o) =>
          o.id === action.payload.orderId
            ? { ...o, status: action.payload.status, updatedAt: new Date().toISOString() }
            : o,
        ),
        detail: state.detail && state.detail.id === action.payload.orderId
          ? { ...state.detail, status: action.payload.status, updatedAt: new Date().toISOString() }
          : state.detail,
      };

    case CO.STATUS_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CO.SEND_REQUEST:
      return { ...state, sending: true, sendingError: null };

    case CO.SEND_SUCCESS:
      return { ...state, sending: false };

    case CO.SEND_FAILURE:
      return { ...state, sending: false, sendingError: action.payload.error };

    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// CUSTOMERS REDUCER
// ════════════════════════════════════════════════════════════════════════════════

const customersInitialState: CateringCustomersState = {
  customers: [],
  loading: false,
  detailLoading: false,
  error: null,
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
  saving: false,
  savingError: null,
  deleting: false,
};

function upsertCustomer(
  customers: CateringCustomersState['customers'],
  next: CateringCustomersState['customers'][number],
) {
  const exists = customers.some((c) => c.id === next.id);
  return exists ? customers.map((c) => (c.id === next.id ? next : c)) : [next, ...customers];
}

export function cateringCustomersReducer(
  state: CateringCustomersState = customersInitialState,
  action: CateringCustomersAction,
): CateringCustomersState {
  switch (action.type) {
    case CC.FETCH_REQUEST:
      return { ...state, loading: true, error: null };

    case CC.FETCH_SUCCESS: {
      const p = action.payload.page;
      return {
        ...state,
        loading: false,
        customers: action.payload.customers,
        total: p?.total ?? action.payload.total,
        page: p?.page ?? state.page,
        limit: p?.limit ?? state.limit,
        totalPages: p?.totalPages ?? state.totalPages,
      };
    }

    case CC.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    case CC.FETCH_DETAIL_REQUEST:
      return { ...state, detailLoading: true };

    case CC.FETCH_DETAIL_SUCCESS:
      return {
        ...state,
        detailLoading: false,
        customers: upsertCustomer(state.customers, action.payload.customer),
      };

    case CC.FETCH_DETAIL_FAILURE:
      return { ...state, detailLoading: false };

    case CC.UPDATE_REQUEST:
      return { ...state, saving: true, savingError: null };

    case CC.UPDATE_SUCCESS: {
      const { customerId, patch } = action.payload;
      return {
        ...state,
        saving: false,
        customers: state.customers.map((c) => {
          if (c.id !== customerId) return c;
          const next = { ...c };
          if (patch.firstName !== undefined) next.firstName = patch.firstName;
          if (patch.lastName !== undefined) next.lastName = patch.lastName;
          if (patch.phone !== undefined) next.phone = patch.phone;
          if (patch.email !== undefined) next.email = patch.email;
          if (patch.company !== undefined) next.company = patch.company;
          if (patch.dietaryNotes !== undefined) next.dietaryNotes = patch.dietaryNotes;
          if (patch.addedTags?.length) next.tags = [...new Set([...c.tags, ...patch.addedTags])];
          if (patch.removedTags?.length) next.tags = next.tags.filter((t) => !patch.removedTags!.includes(t));
          return next;
        }),
      };
    }

    case CC.UPDATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CC.DELETE_REQUEST:
      return { ...state, deleting: true };

    case CC.DELETE_SUCCESS: {
      const nextTotal = Math.max(0, state.total - 1);
      return {
        ...state,
        deleting: false,
        customers: state.customers.filter((c) => c.id !== action.payload.customerId),
        total: nextTotal,
        totalPages: Math.ceil(nextTotal / state.limit) || 1,
      };
    }

    case CC.DELETE_FAILURE:
      return { ...state, deleting: false };

    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// INVOICES REDUCER
// ════════════════════════════════════════════════════════════════════════════════

const invoicesInitialState: CateringInvoicesState = {
  invoices: [],
  loading: false,
  error: null,
  loadedLocationId: null,
  lastFetchedAt: null,
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 1,
  saving: false,
  savingError: null,
  detail: null,
  detailLoading: false,
  detailError: null,
  notifying: false,
  notifyingError: null,
  lastNotifyResult: null,
  sendingReminder: false,
  sendReminderError: null,
  lastReminderResult: null,
  statusUpdating: false,
  statusUpdateError: null,
  recordingPayment: false,
  recordPaymentError: null,
};

export function cateringInvoicesReducer(
  state: CateringInvoicesState = invoicesInitialState,
  action: CateringInvoicesAction,
): CateringInvoicesState {
  switch (action.type) {

    case CI.FETCH_REQUEST:
      return { ...state, loading: true, error: null };

    case CI.FETCH_SUCCESS: {
      const p = action.payload.page;
      return {
        ...state,
        loading: false,
        invoices: action.payload.invoices,
        loadedLocationId: action.payload.locationId,
        lastFetchedAt: Date.now(),
        page: p?.page ?? state.page,
        limit: p?.limit ?? state.limit,
        total: p?.total ?? action.payload.invoices.length,
        totalPages: p?.totalPages ?? state.totalPages,
      };
    }

    case CI.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    case CI.CREATE_REQUEST:
      return { ...state, saving: true, savingError: null, lastNotifyResult: null };

    case CI.CREATE_SUCCESS: {
      const nextTotal = state.total + 1;
      return {
        ...state,
        saving: false,
        invoices: [action.payload.invoice, ...state.invoices],
        total: nextTotal,
        totalPages: Math.ceil(nextTotal / state.limit) || 1,
      };
    }

    case CI.CREATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CI.UPDATE_REQUEST:
      return { ...state, saving: true, savingError: null, lastNotifyResult: null };

    case CI.UPDATE_SUCCESS: {
      const { invoice } = action.payload;
      return {
        ...state,
        saving: false,
        invoices: state.invoices.map((inv) =>
          inv.id === invoice.id ? { ...inv, ...invoice } : inv,
        ),
        detail: state.detail?.id === invoice.id
          ? { ...state.detail, ...invoice }
          : state.detail,
      };
    }

    case CI.UPDATE_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error };

    case CI.FETCH_DETAIL_REQUEST:
      // Keep the current detail when re-fetching the SAME invoice (post-action reloads) so the
      // page doesn't flicker back to the sparse list row; clear it only when loading a different one.
      return {
        ...state,
        detailLoading: true,
        detailError: null,
        detail: state.detail?.id === action.payload.id ? state.detail : null,
      };

    case CI.FETCH_DETAIL_SUCCESS:
      return { ...state, detailLoading: false, detail: action.payload.invoice };

    case CI.FETCH_DETAIL_FAILURE:
      return { ...state, detailLoading: false, detailError: action.payload.error };

    case CI.NOTIFY_REQUEST:
      return { ...state, notifying: true, notifyingError: null, lastNotifyResult: null };

    case CI.NOTIFY_SUCCESS: {
      const { invoiceId, sent, skipped, status, issuedAt } = action.payload;
      const lastNotifyResult = { sent, skipped };
      // Nothing went out: the invoice keeps its previous status (e.g. stays draft).
      if (!sent || !issuedAt) return { ...state, notifying: false, lastNotifyResult };
      const applyNotify = (inv: any) =>
        inv.id === invoiceId
          ? { ...inv, status: status as InvoiceStatus, sentAt: issuedAt, updatedAt: issuedAt }
          : inv;
      return {
        ...state,
        notifying: false,
        lastNotifyResult,
        invoices: state.invoices.map(applyNotify),
        detail: state.detail?.id === invoiceId ? applyNotify(state.detail) : state.detail,
      };
    }

    case CI.NOTIFY_FAILURE:
      return { ...state, notifying: false, notifyingError: action.payload.error, lastNotifyResult: null };

    case CI.SEND_REMINDER_REQUEST:
      return { ...state, sendingReminder: true, sendReminderError: null, lastReminderResult: null };

    case CI.SEND_REMINDER_SUCCESS:
      return {
        ...state,
        sendingReminder: false,
        lastReminderResult: { sent: action.payload.sent, skipped: action.payload.skipped },
      };

    case CI.SEND_REMINDER_FAILURE:
      return { ...state, sendingReminder: false, sendReminderError: action.payload.error, lastReminderResult: null };

    case CI.STATUS_ACTION_REQUEST:
      return { ...state, statusUpdating: true, statusUpdateError: null };

    case CI.STATUS_ACTION_SUCCESS: {
      const { invoiceId, status, updatedAt } = action.payload;
      const applyStatus = (inv: any) =>
        inv.id === invoiceId ? { ...inv, status: status as InvoiceStatus, updatedAt } : inv;
      return {
        ...state,
        statusUpdating: false,
        invoices: state.invoices.map(applyStatus),
        detail: state.detail?.id === invoiceId ? applyStatus(state.detail) : state.detail,
      };
    }

    case CI.STATUS_ACTION_FAILURE:
      return { ...state, statusUpdating: false, statusUpdateError: action.payload.error };

    case CI.RECORD_PAYMENT_REQUEST:
      return { ...state, recordingPayment: true, recordPaymentError: null };

    case CI.RECORD_PAYMENT_SUCCESS: {
      const { invoiceId, payment, status, amountPaid, outstandingBalance, updatedAt } = action.payload;
      // Apply the server-reconciled roll-up. The full payment-schedule reconciliation
      // is refreshed by the saga re-fetching the detail; here we update the summary
      // fields + append the payment for immediate feedback.
      const applyRollup = (inv: any) => {
        if (inv.id !== invoiceId) return inv;
        return {
          ...inv,
          status: status as InvoiceStatus,
          amountPaid,
          outstandingBalance,
          updatedAt,
          payments: [...(inv.payments ?? []), payment],
        };
      };
      return {
        ...state,
        recordingPayment: false,
        invoices: state.invoices.map(applyRollup),
        detail: state.detail?.id === invoiceId ? applyRollup(state.detail) : state.detail,
      };
    }

    case CI.RECORD_PAYMENT_FAILURE:
      return { ...state, recordingPayment: false, recordPaymentError: action.payload.error };

    case CI.ADD_PAYMENT_LOCAL: {
      const { invoiceId, payment } = action.payload;
      const newPayment = { ...payment, id: `pay-${Date.now()}` };

      const applyPayment = (inv: any) => {
        if (inv.id !== invoiceId) return inv;

        const newAmountPaid = inv.amountPaid + payment.amount;
        const newBalance = inv.total - newAmountPaid;

        const updatedSchedule = inv.paymentSchedule.map((ms: any) => ({ ...ms }));
        let remaining = payment.amount;
        for (const ms of updatedSchedule) {
          if (ms.status === 'paid' || remaining <= 0) continue;
          if (remaining >= ms.amount) {
            ms.status = 'paid';
            ms.paymentId = newPayment.id;
            ms.paidAt = new Date().toISOString();
            remaining -= ms.amount;
          }
        }

        const allPaid = updatedSchedule.every((ms: any) => ms.status === 'paid');
        const somePaid = updatedSchedule.some((ms: any) => ms.status === 'paid');
        let newStatus: InvoiceStatus = inv.status;
        if (newBalance <= 0 || allPaid) newStatus = 'paid_in_full';
        else if (somePaid) newStatus = 'partial_paid';

        return {
          ...inv,
          payments: [...inv.payments, newPayment],
          paymentSchedule: updatedSchedule,
          amountPaid: newAmountPaid,
          outstandingBalance: Math.max(0, newBalance),
          status: newStatus,
          updatedAt: new Date().toISOString(),
        };
      };

      return {
        ...state,
        invoices: state.invoices.map(applyPayment),
        detail: state.detail?.id === invoiceId ? applyPayment(state.detail) : state.detail,
      };
    }

    case CI.ISSUE_REFUND_LOCAL: {
      const { invoiceId, amount, reason, notes } = action.payload;
      const refundPayment = {
        id: `refund-${Date.now()}`,
        amount: -amount,
        // Synthetic local row for a cancellation refund — no method was actually chosen, and the
        // invoice screen renders an empty method as "—".
        method: '',
        reference: `REFUND-${Date.now()}`,
        date: new Date().toISOString(),
        recordedBy: 'Staff',
        isVoided: false,
        notes: `Refund — ${reason.replace(/_/g, ' ')}${notes ? `: ${notes}` : ''}`,
      };

      const applyRefund = (inv: any) => {
        if (inv.id !== invoiceId) return inv;

        const newAmountPaid = inv.amountPaid - amount;
        const newBalance = inv.total - newAmountPaid;

        const updatedSchedule = inv.paymentSchedule.map((ms: any) => ({ ...ms }));
        let refundRemaining = amount;
        for (let i = updatedSchedule.length - 1; i >= 0 && refundRemaining > 0; i--) {
          const ms = updatedSchedule[i];
          if (ms.status !== 'paid') continue;
          if (refundRemaining >= ms.amount) {
            ms.status = 'pending';
            ms.paymentId = undefined;
            ms.paidAt = undefined;
            refundRemaining -= ms.amount;
          }
        }

        return {
          ...inv,
          payments: [...inv.payments, refundPayment],
          paymentSchedule: updatedSchedule,
          amountPaid: Math.max(0, newAmountPaid),
          outstandingBalance: Math.max(0, newBalance),
          status: newAmountPaid <= 0 ? ('sent' as InvoiceStatus) : inv.status,
          updatedAt: new Date().toISOString(),
        };
      };

      return {
        ...state,
        invoices: state.invoices.map(applyRefund),
        detail: state.detail?.id === invoiceId ? applyRefund(state.detail) : state.detail,
      };
    }

    case CI.ADD_COMM_LOG_LOCAL: {
      const { invoiceId, entry } = action.payload;
      const newEntry = {
        ...entry,
        id: `comm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      };

      const applyCommLog = (inv: any) =>
        inv.id === invoiceId
          ? {
              ...inv,
              communicationLog: [...inv.communicationLog, newEntry],
              updatedAt: new Date().toISOString(),
            }
          : inv;

      return {
        ...state,
        invoices: state.invoices.map(applyCommLog),
        detail: state.detail?.id === invoiceId ? applyCommLog(state.detail) : state.detail,
      };
    }

    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// ORDER TYPES REDUCER
// ════════════════════════════════════════════════════════════════════════════════

type OrderTypesAction =
  | ReturnType<typeof AC.fetchOrderTypesRequest>
  | ReturnType<typeof AC.fetchOrderTypesSuccess>
  | ReturnType<typeof AC.fetchOrderTypesFailure>
  | ReturnType<typeof AC.fetchOrderTypeFeesRequest>
  | ReturnType<typeof AC.fetchOrderTypeFeesSuccess>
  | ReturnType<typeof AC.fetchOrderTypeFeesFailure>;

const orderTypesInitialState: CateringOrderTypesState = {
  orderTypes: [],
  loading: false,
  error: null,
  loadedLocationId: null,
  fees: [],
  feesLoadedOrderTypeId: null,
  feesLoading: false,
  feesError: null,
};

export function cateringOrderTypesReducer(
  state: CateringOrderTypesState = orderTypesInitialState,
  action: OrderTypesAction,
): CateringOrderTypesState {
  switch (action.type) {
    case COT.FETCH_REQUEST:
      return { ...state, loading: true, error: null };
    case COT.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        orderTypes: action.payload.orderTypes,
        loadedLocationId: action.payload.locationId,
      };
    case COT.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };
    case COT.FETCH_FEES_REQUEST:
      return { ...state, feesLoading: true, feesError: null };
    case COT.FETCH_FEES_SUCCESS:
      return {
        ...state,
        feesLoading: false,
        fees: action.payload.fees,
        feesLoadedOrderTypeId: action.payload.orderTypeId,
      };
    case COT.FETCH_FEES_FAILURE:
      return { ...state, feesLoading: false, feesError: action.payload.error };
    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// SETTINGS REDUCER
// ════════════════════════════════════════════════════════════════════════════════

type SettingsAction =
  | ReturnType<typeof AC.fetchSettingRequest>
  | ReturnType<typeof AC.fetchSettingSuccess>
  | ReturnType<typeof AC.fetchSettingFailure>
  | ReturnType<typeof AC.fetchMultipleSettingsRequest>
  | ReturnType<typeof AC.fetchMultipleSettingsSuccess>
  | ReturnType<typeof AC.fetchMultipleSettingsFailure>
  | ReturnType<typeof AC.upsertSettingRequest>
  | ReturnType<typeof AC.upsertSettingSuccess>
  | ReturnType<typeof AC.upsertSettingFailure>
  | ReturnType<typeof AC.upsertMultipleSettingsRequest>
  | ReturnType<typeof AC.upsertMultipleSettingsSuccess>
  | ReturnType<typeof AC.upsertMultipleSettingsFailure>;

const settingsInitialState: CateringSettingsState = {
  values: {},
  loading: false,
  saving: false,
  error: null,
};

export function cateringSettingsReducer(
  state: CateringSettingsState = settingsInitialState,
  action: SettingsAction,
): CateringSettingsState {
  switch (action.type) {
    case CS.FETCH_REQUEST:
      return { ...state, loading: true, error: null };
    case CS.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        values: { ...state.values, [action.payload.key]: action.payload.value ?? '' },
      };
    case CS.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };
    case CS.FETCH_BULK_REQUEST:
      return { ...state, loading: true, error: null };
    case CS.FETCH_BULK_SUCCESS: {
      const merged: Record<string, string> = { ...state.values };
      for (const [key, value] of Object.entries(action.payload.settings)) {
        merged[key] = value ?? '';
      }
      return { ...state, loading: false, values: merged };
    }
    case CS.FETCH_BULK_FAILURE:
      return { ...state, loading: false, error: action.payload.error };
    case CS.UPSERT_REQUEST:
      return { ...state, saving: true, error: null };
    case CS.UPSERT_SUCCESS:
      return {
        ...state,
        saving: false,
        values: { ...state.values, [action.payload.key]: action.payload.value },
      };
    case CS.UPSERT_FAILURE:
      return { ...state, saving: false, error: action.payload.error };
    case CS.UPSERT_BULK_REQUEST:
      return { ...state, saving: true, error: null };
    case CS.UPSERT_BULK_SUCCESS: {
      const merged: Record<string, string> = { ...state.values };
      for (const { key, value } of action.payload.entries) merged[key] = value;
      return { ...state, saving: false, values: merged };
    }
    case CS.UPSERT_BULK_FAILURE:
      return { ...state, saving: false, error: action.payload.error };
    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// COMMUNICATION TEMPLATES REDUCER
// ════════════════════════════════════════════════════════════════════════════════

const commTemplatesInitialState: CateringCommTemplatesState = {
  templates: [],
  loading: false,
  error: null,
  saving: false,
  savingError: null,
  savingErrorIs404: false,
  loadedLocationId: null,
  restoring: false,
  restoringError: null,
  tagsByEventType: {},
  tagsLoading: {},
};

export function cateringCommTemplatesReducer(
  state: CateringCommTemplatesState = commTemplatesInitialState,
  action: CateringCommTemplatesAction,
): CateringCommTemplatesState {
  switch (action.type) {
    case CCT.FETCH_REQUEST:
      return { ...state, loading: true, error: null };

    case CCT.FETCH_SUCCESS: {
      const newTags = { ...state.tagsByEventType };
      for (const tpl of action.payload.templates) {
        if (tpl.tags?.length) newTags[tpl.event_type] = tpl.tags;
      }
      return {
        ...state,
        loading: false,
        templates: action.payload.templates,
        loadedLocationId: action.payload.locationId,
        tagsByEventType: newTags,
      };
    }

    case CCT.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload.error };

    case CCT.UPSERT_REQUEST:
      return { ...state, saving: true, savingError: null, savingErrorIs404: false };

    case CCT.UPSERT_SUCCESS: {
      let templates = [...state.templates];
      const newTags = { ...state.tagsByEventType };
      for (const t of action.payload.templates) {
        if (t.tags?.length) newTags[t.event_type] = t.tags;
        const byId = templates.findIndex((e) => e.id === t.id);
        if (byId >= 0) { templates[byId] = t; continue; }
        const byKey = templates.findIndex((e) => e.event_type === t.event_type && e.channel === t.channel);
        if (byKey >= 0) { templates[byKey] = t; } else { templates.push(t); }
      }
      return { ...state, saving: false, savingErrorIs404: false, templates, tagsByEventType: newTags };
    }

    case CCT.UPSERT_FAILURE:
      return { ...state, saving: false, savingError: action.payload.error, savingErrorIs404: action.payload.is404 ?? false };

    case CCT.RESTORE_REQUEST:
      return { ...state, restoring: true, restoringError: null };

    case CCT.RESTORE_SUCCESS: {
      let templates = [...state.templates];
      const newTags = { ...state.tagsByEventType };
      for (const t of action.payload.templates) {
        if (t.tags?.length) newTags[t.event_type] = t.tags;
        const byId = templates.findIndex((e) => e.id === t.id);
        if (byId >= 0) { templates[byId] = t; continue; }
        const byKey = templates.findIndex((e) => e.event_type === t.event_type && e.channel === t.channel);
        if (byKey >= 0) { templates[byKey] = t; } else { templates.push(t); }
      }
      return { ...state, restoring: false, templates, tagsByEventType: newTags };
    }

    case CCT.RESTORE_FAILURE:
      return { ...state, restoring: false, restoringError: action.payload.error };

    case CCT.FETCH_TAGS_REQUEST:
      return {
        ...state,
        tagsLoading: { ...state.tagsLoading, [action.payload.eventType]: true },
      };

    case CCT.FETCH_TAGS_SUCCESS:
      return {
        ...state,
        tagsByEventType: { ...state.tagsByEventType, [action.payload.eventType]: action.payload.tags },
        tagsLoading: { ...state.tagsLoading, [action.payload.eventType]: false },
      };

    case CCT.FETCH_TAGS_FAILURE:
      return {
        ...state,
        tagsLoading: { ...state.tagsLoading, [action.payload.eventType]: false },
      };

    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// RESOLVED NOTIFICATION TEMPLATE REDUCER
// ════════════════════════════════════════════════════════════════════════════════

const resolvedTemplateInitialState: CateringResolvedTemplateState = {
  data: null,
  loading: false,
  error: null,
  notConfigured: false,
  requestKey: null,
  resolvedKey: null,
};

export function cateringResolvedTemplateReducer(
  state: CateringResolvedTemplateState = resolvedTemplateInitialState,
  action: CateringResolvedTemplateAction,
): CateringResolvedTemplateState {
  switch (action.type) {
    case CRT.FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        requestKey: resolvedTemplateKey(action.payload.entity, action.payload.id, action.payload.country),
      };
    case CRT.FETCH_SUCCESS:
      // Ignore a response for a request that's no longer the latest — takeLatest in the
      // saga already cancels it, but guard here too in case ordering ever changes.
      if (action.payload.key !== state.requestKey) return state;
      return { ...state, loading: false, data: action.payload.data, resolvedKey: action.payload.key, error: null, notConfigured: false };
    case CRT.FETCH_FAILURE:
      if (action.payload.key !== state.requestKey) return state;
      return { ...state, loading: false, data: null, resolvedKey: action.payload.key, error: action.payload.error, notConfigured: action.payload.notConfigured };
    case CRT.RESET:
      return resolvedTemplateInitialState;
    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// TAXES REDUCER
// ════════════════════════════════════════════════════════════════════════════════

const taxesInitialState: CateringTaxesState = {
  taxes: [],
  charges: [],
  loading: false,
  error: null,
  loadedKey: null,
};

export function cateringTaxesReducer(
  state: CateringTaxesState = taxesInitialState,
  action: CateringTaxesAction,
): CateringTaxesState {
  switch (action.type) {
    case CT.FETCH_REQUEST:
      return { ...state, loading: true, error: null };
    case CT.FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        taxes: action.payload.taxes,
        charges: action.payload.charges,
        loadedKey: action.payload.loadedKey,
      };
    case CT.FETCH_FAILURE:
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

// ════════════════════════════════════════════════════════════════════════════════
// COMBINED CATERING REDUCER
// ════════════════════════════════════════════════════════════════════════════════

/**
 * Single catering reducer. All catering slices live under one `catering` key:
 *   state.catering.leads, state.catering.estimates, state.catering.orders, …
 */
export const cateringReducer = combineReducers({
  leads: cateringLeadsReducer,
  estimates: cateringEstimatesReducer,
  orderTypes: cateringOrderTypesReducer,
  settings: cateringSettingsReducer,
  orders: cateringOrdersReducer,
  commTemplates: cateringCommTemplatesReducer,
  customers: cateringCustomersReducer,
  taxes: cateringTaxesReducer,
  invoices: cateringInvoicesReducer,
  resolvedTemplate: cateringResolvedTemplateReducer,
  packages: cateringPackagesReducer,
  /** The signed-in employee's Catering permissions (read again on every page visit). */
  access: cateringAccessReducer,
});
